import "../chat-thread";

import { Storybook } from "@tento-ui/ui/storybook";
import { ChatThread } from "../chat-thread";
import type { ChatContentBlock, ChatMessage } from "../chat-types";
import { CodexLook } from "./codex";

// `std-chat-codex-turn` is a core tag whose component lives here, outside
// tento-ui-js. The charter scanner walks only tento-ui-js, and tento-ui-js may
// not import the chat package, so the catalog registers this story itself
// (`CoreRegistry.ADJACENT` in ui.lona.so).
//
// It is one turn of a thread in the Codex CLI's grammar — the user's prompt
// as a full-width band, assistant prose behind a dim bullet, and each tool
// call as a `• Ran …` line with a `└` summary that expands on click.
//
// A turn cannot stand alone: the thread hands it the plugin manager that
// resolves its blocks, and the codex look is what names this element as the
// thread's turn. So every example is a `std-chat-thread` bound to
// `CodexLook`, and every row in it is a `std-chat-codex-turn`.
//
// The package's own "Chat look — Codex CLI" story shows the look; these show
// the turn's states. Commands and output are from this repository.

let nextId = 0;

function user(text: string): ChatMessage {
  return {
    id: `user-${nextId++}`,
    role: "user",
    content: [{ type: "text", text }],
    isStreaming: false,
    createdAt: nextId,
  };
}

function assistant(content: ChatContentBlock[]): ChatMessage {
  return {
    id: `assistant-${nextId++}`,
    role: "assistant",
    content,
    isStreaming: false,
    createdAt: nextId,
  };
}

function ran(
  command: string,
  output: Option<string>,
  status: "completed" | "running" | "failed",
): ChatContentBlock {
  return { type: "tool_call", tool: "shell", command, output, status };
}

const GIT_LOG = [
  "5a8a0fd3f docs(std-core): provide public fixtures for 29 admitted discovery gaps",
  "90570feaa feat(tento-ui): core stories declare their tags",
  "c0a67c188 feat(tento-ui): a chip’s tone fills its box, and a bare mark names the state",
  "1d1f53127 feat(lona-js): std-chip — the small status label, extracted",
].join("\n");

const SESSION: ChatMessage[] = [
  user("why does the chip-row card only show one fixture?"),
  assistant([
    {
      type: "text",
      text:
        "The card renders its story's first example, and the chip-row story " +
        "was a one-example fixture. Its history shows where it came from:",
    },
    ran("zsh -lc 'git log --oneline -4 -- tento-ui-js/components/chip'", GIT_LOG, "completed"),
  ]),
  user("is std-chip-row on the allowlist?"),
  assistant([
    ran(
      "zsh -lc 'grep -n std-chip-row tento-ui-js/std-charter.allowlist'",
      "50:std-chip-row\tcontent\t# tento-ui-js/components/chip/chip-row.ts:13",
      "completed",
    ),
    { type: "text", text: "Yes — admitted under content, so its story becomes the core card." },
  ]),
];

const STATES: ChatMessage[] = [
  assistant([
    { type: "text", text: "Checking the tree, then the three story files." },
    ran("zsh -lc 'tsc -p tsconfig.json --noEmit'", null, "running"),
    ran(
      "zsh -lc 'deno test --no-check tento-ui-js/import-boundary.deno.test.ts'",
      [
        "tento-ui-js never imports the chat library or its old paths ... FAILED (981µs)",
        "error: NotCapable: Requires read access to \"/Users/james/workspace/tento/tento-ui-js\", run again with the --allow-read flag",
      ].join("\n"),
      "failed",
    ),
    ran("zsh -lc 'mkdir -p tento-ui-js/components/adjacent'", null, "completed"),
  ]),
];

const LONG: ChatMessage[] = [
  user(
    "write real stories for std-chip-row, std-code and std-json-viewer so they stop " +
      "showing a single throwaway fixture on the core catalog page",
  ),
  assistant([
    {
      type: "text",
      text:
        "Each story leads with the state that reads best on its own, because the " +
        "collapsed card only ever renders the first example; the rest sit behind the " +
        "expand button. Chip-row's story is about the row rather than the chip — " +
        "wrapping, rebinding and the empty bind that hides it — since chip.example.ts " +
        "already covers every tone.",
    },
  ]),
];

function $thread(messages: ChatMessage[], height: string): ChatThread {
  const $thread = ChatThread.make() as ChatThread;
  $thread.theme = CodexLook;
  // A copy per render: the thread mutates the messages it is given while it
  // streams, and two cards must never share one model.
  void $thread.setMessages(
    messages.map((m) => ({ ...m, content: m.content.map((block) => ({ ...block })) })),
  );
  // No padding on the thread — rows carry their own gutter through the
  // look's --chat-turn-inset. The height is the scroll viewport's.
  return Storybook.fill($thread.assignStyles({
    display: "block",
    height,
    borderRadius: "10px",
    boxSizing: "border-box",
  }));
}

export class CoreChatCodexTurnStoryBook extends Storybook.Default {
  title(): string {
    return "Chat Turn — Codex";
  }

  tags(): string[] {
    return ["std-chat-codex-turn"];
  }

  examples(): Storybook.Example[] {
    return [
      {
        // Two exchanges. Click a `• Ran` entry to expand its output under
        // the connector; click again to fold it.
        title: "Conversation",
        render: () => $thread(SESSION, "360px"),
      },
      {
        // Every state a tool call's summary line carries on its own: in
        // flight (the head pulses), failed (✗ in the connector), and done
        // with nothing printed.
        title: "Tool states",
        render: () => $thread(STATES, "240px"),
      },
      {
        // Prose wraps with a hanging indent: continuation lines sit under
        // the text, not under the bullet.
        title: "Long prose",
        render: () => $thread(LONG, "280px"),
      },
      {
        // The turn as the thread streams into it: prose arrives as text
        // deltas, then a tool call starts running, streams its output and
        // settles; after a pause it starts again from the prompt.
        title: "Streaming",
        render: () => {
          const PROMPT = user("how big is the charter allowlist?");
          const $chat = $thread([PROMPT], "300px");
          let run = 0;

          const play = async () => {
            const token = ++run;
            const id = `assistant-stream-${token}`;
            const live = () => token === run && $chat.isConnected;
            const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

            // The first run waits (up to ten seconds) for the card to mount
            // the thread, so it never races the thread's own initial bind.
            for (let tries = 0; !$chat.isConnected; tries++) {
              if (tries >= 100 || token !== run) return;
              await wait(100);
            }
            if (token > 1) {
              await $chat.setMessages([{ ...PROMPT, content: PROMPT.content.map((b) => ({ ...b })) }]);
            } else {
              await wait(400);
            }
            if (!live()) return;
            await $chat.addMessage({
              id,
              role: "assistant",
              content: [],
              isStreaming: true,
              createdAt: Date.now(),
            });
            $chat.applyDelta({
              type: "content_block_start",
              messageId: id,
              blockIndex: 0,
              block: { type: "text", text: "" },
            });
            const words = "Counting the std- lines in the allowlist.".split(" ");
            for (const word of words) {
              await wait(70);
              if (!live()) return;
              $chat.applyDelta({
                type: "text_delta",
                messageId: id,
                blockIndex: 0,
                delta: (word === words[0] ? "" : " ") + word,
              });
            }

            const command = "zsh -lc 'grep -c std- tento-ui-js/std-charter.allowlist'";
            await wait(300);
            if (!live()) return;
            $chat.applyDelta({
              type: "content_block_start",
              messageId: id,
              blockIndex: 1,
              block: ran(command, null, "running"),
            });
            await wait(1200);
            if (!live()) return;
            $chat.applyDelta({ type: "text_delta", messageId: id, blockIndex: 1, delta: "208" });
            $chat.applyDelta({
              type: "content_block_start",
              messageId: id,
              blockIndex: 1,
              block: ran(command, "208", "completed"),
            });

            await wait(300);
            if (!live()) return;
            $chat.applyDelta({
              type: "content_block_start",
              messageId: id,
              blockIndex: 2,
              block: { type: "text", text: "208 lines of the allowlist mention std-." },
            });
            $chat.applyDelta({ type: "message_stop", messageId: id });

            await wait(3000);
            if (live()) void play();
          };

          void play();
          return $chat;
        },
      },
    ];
  }
}
